// GAP-15e — first-week execution plan (OddsJam Wave 1).
//
// Pure presentational. No DB/RPC/migration. Standalone auth-gated
// /onboarding/first-week, NOT in steps.ts (orientation, not a legal
// gate). A deterministic 7-day ORIENTATION scaffold — what to do and
// what to expect — NOT a fabricated pick list or promised return.
// Honest by construction: it points at real products and the
// process; it never invents a specific bet, stake figure, or P&L.

const WRAP: React.CSSProperties = {
  maxWidth: 680,
  margin: "0 auto",
  padding: "36px 24px 80px",
  color: "var(--se-text, #fff)",
  fontFamily: "var(--se-font-body, system-ui)",
};

interface Day {
  readonly day: string;
  readonly focus: string;
  readonly detail: string;
}

// Process-oriented, product-agnostic. Stakes come from the user's
// own declared bankroll via Kelly (GAP-03) — never hardcoded here.
const PLAN: readonly Day[] = [
  {
    day: "Day 1",
    focus: "Set the foundation",
    detail:
      "Confirm Safe Mode limits and declare your bankroll. Every " +
      "stake STRIKZA suggests is sized from that figure — no figure, " +
      "no sizing.",
  },
  {
    day: "Day 2",
    focus: "Read one signal end-to-end",
    detail:
      "Open a Fair-Price signal. Look at offered vs no-vig fair price " +
      "and the edge — understand WHY before you act on anything.",
  },
  {
    day: "Day 3",
    focus: "Place one, with discipline",
    detail:
      "Take a single Sika-Pro+ signal at the Kelly-sized stake. One " +
      "bet, correctly sized, beats ten by feel.",
  },
  {
    day: "Day 4",
    focus: "Log it — outcome irrelevant",
    detail:
      "Record the price you took. Whether it wins is noise; whether " +
      "you beat the closing line is the signal.",
  },
  {
    day: "Day 5",
    focus: "Review process, not P&L",
    detail:
      "Did you size from bankroll? Take a +EV price? Avoid suspended " +
      "or zero-motivation markets? Score the decision, not the result.",
  },
  {
    day: "Day 6",
    focus: "Add a second surface",
    detail:
      "Explore the Singles slot or the Ladder. Same discipline, " +
      "different cadence. Don't chase volume — extend the habit.",
  },
  {
    day: "Day 7",
    focus: "Reflect",
    detail:
      "Look back at your week as a process scorecard. Consistent " +
      "correct decisions compound. STRIKZA never promises wins — it " +
      "makes correct decisions repeatable.",
  },
];

export function FirstWeekPlanPage(): JSX.Element {
  return (
    <div data-testid="first-week-plan" style={WRAP}>
      <div
        style={{
          fontFamily: "var(--se-font-mono, monospace)",
          fontSize: 10,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--se-text-muted, #888)",
        }}
      >
        Orientation · optional
      </div>
      <h1 style={{ fontSize: 25, fontWeight: 500, margin: "12px 0 6px" }}>
        Your first week
      </h1>
      <p
        style={{
          fontSize: 13.5,
          lineHeight: 1.6,
          color: "var(--se-text-sec, #aaa)",
          margin: "0 0 28px",
        }}
      >
        A process scaffold — what to do and what to expect. It points
        at real products; it never hands you a pick or a promised
        return. Stakes are sized from your declared bankroll, not
        prescribed here.
      </p>

      <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {PLAN.map((d) => (
          <li
            key={d.day}
            data-testid={`fwp-${d.day.replace(/\s+/g, "-").toLowerCase()}`}
            style={{
              display: "grid",
              gridTemplateColumns: "70px 1fr",
              gap: 14,
              padding: "14px 0",
              borderTop: "1px solid var(--se-border, #2a2a2a)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--se-font-mono, monospace)",
                fontSize: 12,
                color: "var(--se-amber, #d4a017)",
              }}
            >
              {d.day}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                {d.focus}
              </div>
              <div
                style={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: "var(--se-text-sec, #aaa)",
                }}
              >
                {d.detail}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
